import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isOnHomePage = location.pathname === '/';

  const menuItems = [
    { name: 'La Callejoneada', to: 'callejoneada', external: !isOnHomePage },
    { name: 'La Boda', to: 'boda', external: !isOnHomePage },
    { name: 'RSVP', to: 'rsvp', external: !isOnHomePage },
    {
      name: 'Mesa de Regalos',
      to: '/mesa-de-regalos',
      external: location.pathname !== '/mesa-de-regalos',
    },
    { name: 'Hospedaje', to: 'hospedaje', external: !isOnHomePage },
  ];

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!isOnHomePage) return;

      const sections = document.querySelectorAll('section');
      let current: string | null = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - sectionHeight / 3) {
          current = section.getAttribute('id');
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOnHomePage]);

  // Scroll to section when navigating back to the home page
  useEffect(() => {
    if (isOnHomePage && location.hash) {
      const sectionId = location.hash.replace('#', '');
      scrollToSection(sectionId);
    }
  }, [isOnHomePage, location.hash]);

  return (
    <nav className="relative z-50">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed bottom-0 left-1/2 transform -translate-x-1/2 z-50">
        <button
          className={`menu cross menu--1 w-24 h-12 bg-[#609966] bg-opacity-90 rounded-t-full flex justify-center items-center transform transition-transform duration-300 ease-in-out shadow-none focus:outline-none focus:ring-0 border-none ${isMenuOpen ? 'is-open' : ''}`}
          type="button"
          onClick={toggleMenu}
        >
          <label>
            <svg
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-class"
            >
              <path className="line line--1" d="M35 45h30" />
              <path className="line line--2" d="M35 55h30" />
              <path className="line line--3" d="M35 65h30" />
            </svg>
          </label>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 bg-black bg-opacity-90 z-40 flex flex-col justify-center items-center text-white transition-transform duration-[500ms] ease-out transform ${
          isMenuOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <ul className="text-center uppercase tracking-[2.9px] text-lg space-y-6">
          {menuItems.map((item, index) => (
            <li
              key={item.to}
              style={{
                transitionDelay: `${isMenuOpen ? 500 + index * 300 : 0}ms`,
              }}
              className={`opacity-0 transform translate-y-4 -translate-x-full transition-all duration-500 ease-out ${
                isMenuOpen ? 'opacity-100 translate-y-0 translate-x-0' : ''
              }`}
            >
              {item.external ? (
                <Link
                  onClick={() => toggleMenu()}
                  to={`${item.to.startsWith('/') ? item.to : `/#${item.to}`}`}
                  className="hover:text-pink-500"
                >
                  {item.name}
                </Link>
              ) : (
                <a
                  onClick={() => toggleMenu()}
                  href={`#${item.to}`}
                  className={`hover:text-pink-500 ${
                    activeSection === item.to ? 'text-pink-500 font-bold' : ''
                  }`}
                >
                  {item.name}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Desktop Navbar */}
      <div className="hidden md:flex fixed top-0 left-0 w-full bg-black bg-opacity-50 text-white uppercase tracking-[2.9px] py-4 z-20 justify-center space-x-6">
        {menuItems.map((item) =>
          item.external ? (
            <Link
              key={item.to}
              to={`${item.to.startsWith('/') ? item.to : `/#${item.to}`}`}
              className="hover:text-pink-500"
            >
              {item.name}
            </Link>
          ) : (
            <a
              key={`#${item.to}`}
              href={`#${item.to}`}
              className={`hover:text-pink-500 ${
                activeSection === item.to ? 'text-pink-500 font-bold' : ''
              }`}
            >
              {item.name}
            </a>
          )
        )}
      </div>
    </nav>
  );
};

export default Navbar;
